import {Action} from '@reduxjs/toolkit'
import {EmployeeModel} from '../models/Employee'
import { getEmployees } from './EmployeeCRUD'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  EmployeesRequested: '[Request Employees] Action',
  EmployeesLoaded: '[Load Employees] Employees API',
  EmployeesFailed: '[Load Employees] Employees API Failed',
  EmployeeRequested: '[Request Employee] Action',
  EmployeeLoaded: '[Load Employee] Employee API',
  EmployeeFailed: '[Load Employee] Employee API Failed',
  EmployeeCreated: '[Create Employee] Action',
  EmployeeUpdated: '[Update Employee] Action',
  EmployeeDeleted: '[Delete Employee] Action',
}

const initialEmployeeState: IEmployeeState = {
  employees: [],
  employee: undefined,
  loading: false,
  error: undefined,
}

export interface IEmployeeState {
  employees: EmployeeModel[]
  employee?: EmployeeModel
  loading: boolean
  error?: string
}

export const reducer = (state: IEmployeeState = initialEmployeeState, action: ActionWithPayload<IEmployeeState>) => {
  switch (action.type) {
    case actionTypes.EmployeesRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.EmployeesLoaded: {
      const employees = action.payload?.employees || []
      return {...state, employees, loading: false, error: undefined}
    }

    case actionTypes.EmployeesFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.EmployeeRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.EmployeeLoaded: {
      const employee = action.payload?.employee
      return {...state, employee, loading: false, error: undefined}
    }

    case actionTypes.EmployeeFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.EmployeeCreated: {
      const employee = action.payload?.employee
      if (employee) {
        return {...state, employees: [...state.employees, employee], loading: false}
      }
      return state
    }

    case actionTypes.EmployeeUpdated: {
      const employee = action.payload?.employee
      if (employee) {
        const updatedEmployees = state.employees.map(e => (e.id === employee.id ? employee : e))
        return {...state, employees: updatedEmployees, employee, loading: false}
      }
      return state
    }

    case actionTypes.EmployeeDeleted: {
      const employeeId = action.payload?.employee?.id
      if (employeeId) {
        const filteredEmployees = state.employees.filter(e => e.id !== employeeId)
        return {...state, employees: filteredEmployees, loading: false}
      }
      return state
    }

    default:
      return state
  }
}

export const actions = {
  requestEmployees: () => ({
    type: actionTypes.EmployeesRequested,
  }),
  fulfillEmployees: (employees: EmployeeModel[]) => ({
    type: actionTypes.EmployeesLoaded,
    payload: {employees},
  }),
  failEmployees: (error: string) => ({
    type: actionTypes.EmployeesFailed,
    payload: {error},
  }),
  requestEmployee: () => ({
    type: actionTypes.EmployeeRequested,
  }),
  fulfillEmployee: (employee: EmployeeModel) => ({
    type: actionTypes.EmployeeLoaded,
    payload: {employee},
  }),
  failEmployee: (error: string) => ({
    type: actionTypes.EmployeeFailed,
    payload: {error},
  }),
  createEmployee: (employee: EmployeeModel) => ({
    type: actionTypes.EmployeeCreated,
    payload: {employee},
  }),
  updateEmployee: (employee: EmployeeModel) => ({
    type: actionTypes.EmployeeUpdated,
    payload: {employee},
  }),
  deleteEmployee: (employee: EmployeeModel) => ({
    type: actionTypes.EmployeeDeleted,
    payload: {employee},
  }),
}

// Thunk function to fetch employees from API
export const fetchEmployeesThunk = (params?: { search?: string; company?: string }) => async (dispatch: any, getState: any) => {
  try {
    const state = getState();
    const employeesState = state?.employees;
    const hasEmployees = Array.isArray(employeesState?.employees) && employeesState.employees.length > 0;
    const isLoading = Boolean(employeesState?.loading);

    if (hasEmployees || isLoading) {
      return;
    }

    dispatch(actions.requestEmployees());
    const shouldUseFilters = Boolean(params?.search || params?.company);
    const response = await getEmployees(
      shouldUseFilters
        ? {
            search: params?.search,
            company: params?.company,
          }
        : undefined
    );
    const employees = response.data.results || response.data || [];
    dispatch(actions.fulfillEmployees(employees));
  } catch (error: any) {
    dispatch(actions.failEmployees(error.message || 'Failed to fetch employees'));
  }
}; 